import React, { useState, useEffect, useMemo } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import Joi from 'joi'
import { useToasts } from 'react-toast-notifications'
import { MdEdit } from 'react-icons/md'

import MainModal from '../../../../components/dashboard/modal/MainModal'
import Flex from '../../../../components/layouts/Flex'

import { CreatableSelectComponent } from '../../../../components/SelectDropdown'
import StaticTable from '../../../../components/StaticTable'
import DashboardInput from '../../../../components/dashboard/DashboardInput'
import DashboardButton from '../../../../components/dashboard/DashboardButton'
import EmptyWrapper from '../../../../components/EmptyWrapper'

import axiosInstance from '../../../../services/config/AxiosIntance'

import useFetch from '../../../../hooks/useFetch'

import {
  setErrorMessage,
} from '../../../../store/actions/ratingActions'

const UpdateRatingMatrix = () => {
  const dispatch = useDispatch()

  let { data: ratingMatrixTypeResponse, refetch } = useFetch(
    '/rating-matrix-types'
  )

  ratingMatrixTypeResponse = ratingMatrixTypeResponse?.resp.map((e) => ({
    label: e.name,
    value: e.id,
  }))

  const [ratingMatrixType, setRatingMatrixType] = useState({})
  const [ratingMatrix, setRatingMatrix] = useState([])
  const [saveLoading, setSaveLoading] = useState(false)

  const { addToast } = useToasts()

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Action',
        accessor: 'action',
        Cell: (value) => {
          return (
            <Flex gap="0.5rem" justify="center">
              <MdEdit onClick={() => handleEditRow(value?.row?.original)} />
            </Flex>
          )
        },
      },
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Description',
        accessor: 'description',
      },
      {
        Header: 'Min. Score',
        accessor: 'min_score',
      },
      {
        Header: 'Max. Score',
        accessor: 'max_score',
      },
    ],
    []
  )

  const { loading, selectedRecord } = useSelector((state) => state.ratings)

  const validationSchema = useMemo(() => {
    return Joi.object({
      name: Joi.string().label('Name').required(),
      description: Joi.string().label('Description').required(),
      min_score: Joi.number()
        .label('Min. Score')
        .required()
        .max(Joi.ref('max_score'))
        .messages({
          'number.max':
            'Min. Score must be smaller than or equal to Max. Score',
        }),

      max_score: Joi.number().label('Max. Score').required(),
      rating_matrix_type_id: Joi.string()
        .label('Rating Matrix Type')
        .required(),
    })
  }, [])

  const [fieldErrors, setFieldErrors] = useState([])

  const [id, setId] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [minScore, setMinScore] = useState(0)
  const [maxScore, setMaxScore] = useState(5)

  const handleSaveMatrix = () => {
    if (saveLoading) return

    const payload = {
      name,
      description,
      min_score: minScore,
      max_score: maxScore,
      rating_matrix_type_id: ratingMatrixType?.value?.toString(),
    }

    const result = validationSchema.validate(payload, {
      abortEarly: false,
    })

    if (result.error) {
      const { error } = result
      const fieldNames = Object.keys(payload)
      const updatedErrors = fieldNames.reduce((acc, fieldName) => {
        acc[fieldName] =
          error?.details
            .find((detail) => detail.context.key === fieldName)
            ?.message.replace(/"/g, '') || ''
        return acc
      }, {})

      setFieldErrors(updatedErrors)

      dispatch(
        setErrorMessage(
          'Please correct the validation errors first before proceeding.'
        )
      )
      return
    }

    if (id) {
      setSaveLoading(true)
      axiosInstance
        .put(`/rating-matrix/${id}/update`, payload)
        .then((response) => {
          const updated = response?.data?.data

          setRatingMatrix((prev) =>
            prev.map((item) =>
              Number(item.id) === Number(updated.id)
                ? { ...item, ...updated }
                : item
            )
          )

          addToast(response?.data?.message, { appearance: 'success' })
        })
        .catch(() => {
          addToast('Failed to update rating matrix', { appearance: 'error' })
        })
    } else {
      axiosInstance
        .post('/rating-matrix', payload)
        .then((response) => {
          setRatingMatrix((matrix) => [...matrix, response?.data?.data])
          addToast(response?.data?.message, { appearance: 'success' })
        })
        .catch(() => {
          addToast('Failed to create rating matrix', { appearance: 'error' })
        })
    }
    setSaveLoading(false)

    setId(null)
    setName('')
    setDescription('')
    setMinScore(0)
    setMaxScore(5)
    setFieldErrors({})
  }

  const handleEditRow = (row) => {
    setId(row?.id)
    setDescription(row?.description)
    setName(row?.name)
    setMinScore(row?.min_score)
    setMaxScore(row?.max_score)
  }

  const handleRatingMatrixType = (e) => {
    if (e.value === e.label) {
      axiosInstance
        .post('/rating-matrix-type', { name: e.value })
        .then((response) => {
          const result = response?.data
          addToast(result?.message, { appearance: 'success' })

          refetch()
          setRatingMatrixType({
            value: result?.data?.id,
            label: result?.data?.name,
          })
        })
    }
    setRatingMatrixType(e)
  }



  useEffect(() => {
    setRatingMatrixType({
      value: selectedRecord?.ratingQuestion?.rating_matrix_type_id,
      label: selectedRecord?.ratingQuestion?.rating_matrix_type_name,
    })
  }, [selectedRecord?.ratingQuestion])

  useEffect(() => {
    if (Number(ratingMatrixType?.value)) {
      axiosInstance
        .get(`/rating-matrix`, {
          params: { rating_matrix_type_id: ratingMatrixType?.value },
        })
        .then((resp) => {
          if (resp?.statusText === 'OK') {
            setRatingMatrix(resp?.data?.resp)
          }
        })
    }
    setRatingMatrix([])
  }, [ratingMatrixType?.value])

  return (
    <MainModal
      title="Update Rating Matrix"
      loading={loading}
      buttonText="Close"
    >
      <Flex gap="0.4rem">
        <CreatableSelectComponent
          title="Rating Matrix Type"
          isMulti={false}
          placeholder="Select or Create a New Rating Matrix Type"
          options={ratingMatrixTypeResponse}
          selected={ratingMatrixType}
          onChange={handleRatingMatrixType}
          wrapperClass={fieldErrors?.rating_matrix_type_id && 'error'}
          subText={fieldErrors?.rating_matrix_type_id}
        />
      </Flex>
      <Flex justify="center">
        {ratingMatrix?.length > 0 ? (
          <StaticTable columns={tableColumns} data={ratingMatrix} />
        ) : (
          <EmptyWrapper message="No rating matrix" />
        )}
      </Flex>
      <Flex gap="0.4rem">
        <DashboardInput
          errorMessage={fieldErrors.name}
          label="Name"
          placeholder="Enter Name"
          handleChange={(e) => setName(e.target.value)}
          value={name}
        />
        <DashboardInput
          errorMessage={fieldErrors.description}
          label="Description"
          placeholder="Enter Description"
          handleChange={(e) => setDescription(e.target.value)}
          value={description}
        />
      </Flex>
      <Flex gap="1rem">
        <DashboardInput
          errorMessage={fieldErrors.min_score}
          label="Min. Score"
          type="number"
          min={0}
          max={maxScore}
          value={minScore}
          handleChange={(e) => setMinScore(e.target.value)}
          placeholder="Enter Min. Score"
        />
        <DashboardInput
          errorMessage={fieldErrors.max_score}
          label="Max. Score"
          type="number"
          min={minScore}
          max={5}
          handleChange={(e) => setMaxScore(e.target.value)}
          value={maxScore}
          placeholder="Enter Max. Score"
        />
      </Flex>
      <Flex>
        <DashboardButton
          text={saveLoading ? 'Saving...' : 'Save Matrix'}
          type="button"
          notIcon
          maxWidth="180px"
          onClick={handleSaveMatrix}
        />
      </Flex>
    </MainModal>
  )
}

export default UpdateRatingMatrix
